import { useRef } from 'react';

import type { HotspotSlice, Point } from '@/chat/activity';
import { centreOf, hotspotZones, zoneAt } from '@/chat/activity';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { HintArea, hintArea, pointAt, Stage, ZoneButton } from './stage';

/**
 * Tap the rip. One tap drops a pin; the pin is the answer, and the zone it
 * landed in is what the receipt bubble quotes.
 *
 * The keyboard path is the authored regions themselves: tabbing through them
 * and pressing Enter puts the pin in the middle of one, which is as precise as
 * the marking is.
 *
 * `hint` dims the picture outside a loose box around the right region.
 */

export type HotspotSurfaceProps = {
  slice: HotspotSlice;
  point: Point | null;
  armed: boolean;
  revealed: boolean;
  hint: boolean;
  onChange: (point: Point) => void;
};

export function HotspotSurface({ slice, point, armed, revealed, hint, onChange }: HotspotSurfaceProps) {
  const { chat } = useStrings();
  const stage = useRef<HTMLDivElement>(null);
  const zones = hotspotZones(slice);
  const landed = point === null ? null : zoneAt(zones, point);
  const area = hint && !revealed ? hintArea(zones) : null;

  return (
    <>
      <Stage
        image={slice.primary.image}
        revealImage={slice.primary.reveal_image}
        revealed={revealed}
        interactive={armed}
        stageRef={stage}
        onPointerDown={
          armed
            ? (event) => {
                if (stage.current) {
                  onChange(pointAt(stage.current, event));
                }
              }
            : undefined
        }
      >
        {zones.map((zone, index) => (
          // Numbered rather than named, so the keyboard label does not give
          // the answer away.
          <ZoneButton
            key={`${zone.label}-${index}`}
            zone={zone}
            label={formatString(chat.chat_activity_region, { current: index + 1, total: zones.length })}
            onSelect={() => onChange(centreOf(zone))}
            disabled={!armed}
            highlighted={revealed && zone.correct}
          />
        ))}

        {area ? <HintArea zone={area} label={chat.chat_activity_hint_area} /> : null}

        {point === null ? null : (
          // Keyed on the position so a new tap replays the drop rather than sliding.
          <Pin key={`${point.x}-${point.y}`} point={point} />
        )}
      </Stage>

      <p aria-live="polite" className="sr-only">
        {point === null
          ? chat.chat_activity_pin_cleared
          : formatString(chat.chat_activity_pin_placed, {
              label: landed?.label ?? chat.chat_activity_elsewhere,
            })}
      </p>
    </>
  );
}

/** The dropped pin: a map marker whose tip is the point, so it never covers it. */
function Pin({ point }: { point: Point }) {
  return (
    <span
      aria-hidden
      style={{ left: `${point.x}%`, top: `${point.y}%` }}
      // The outer span holds the position; the inner one animates, so the drop
      // keyframe cannot overwrite the centring translate.
      className="absolute -translate-x-1/2 -translate-y-full"
    >
      <svg width="30" height="38" viewBox="0 0 30 38" className="activity-pin-drop drop-shadow-md">
        <path
          d="M15 37C15 37 2 21 2 14a13 13 0 0 1 26 0c0 7-13 23-13 23Z"
          className="fill-surf-red"
          stroke="white"
          strokeWidth="2.5"
        />
        <circle cx="15" cy="14" r="4.5" fill="white" />
      </svg>
    </span>
  );
}
